import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { CarePlanItemCardComponent } from '../../shared/care-plan-item-card/care-plan-item-card.component';
import { CarePlanStatusSelectComponent } from '../../shared/care-plan-status-select/care-plan-status-select.component';
import { CarePlanAction, isClosedActionStatus } from '../actions.model';
import { CarePlanActionsStore } from '../actions.store';
import { formatActionDate, isActionOverdue } from '../action-date';
import { ActionModalEntryPoint } from '../action-editor-source';
import { ActionModalService } from '../action-modal.service';

@Component({
  selector: 'bn-action-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CarePlanItemCardComponent, CarePlanStatusSelectComponent],
  templateUrl: './action-card.component.html',
  styleUrl: './action-card.component.scss',
})
export class ActionCardComponent {
  private readonly _actionModal = inject(ActionModalService, {
    optional: true,
  });
  private readonly _messages = inject(MessageService, { optional: true });

  readonly entryPoint = input.required<ActionModalEntryPoint>();
  readonly action = input.required<CarePlanAction>();
  readonly store = input.required<CarePlanActionsStore>();
  readonly readonly = input(false);
  readonly caseDomainLabel = input<string | null>(null);
  readonly showGoToCase = input(false);
  readonly goToCase = output<string>();

  protected readonly displayedStatusCode = linkedSignal(
    () => this.action().statusCode,
  );
  protected readonly statusOptions = computed(() => this.store().statuses());
  protected readonly ownerLabel = computed(() =>
    this.store().ownerLabel(this.action().assignedToId),
  );
  protected readonly typeLabel = computed(() =>
    this.store().actionTypeLabel(this.action().actionTypeCode),
  );
  protected readonly statusLabel = computed(() =>
    this.store().statusLabel(this.action().statusCode),
  );
  protected readonly dueDateLabel = computed(() =>
    formatActionDate(this.action().dueDate),
  );
  protected readonly completedDateLabel = computed(() =>
    this.action().completedAt
      ? formatActionDate(this.action().completedAt)
      : null,
  );
  protected readonly isClosed = computed(() =>
    isClosedActionStatus(this.action().statusCode),
  );
  protected readonly isOverdue = computed(() =>
    isActionOverdue(this.action().dueDate, this.action().statusCode),
  );

  async onStatusChange(statusCode: string): Promise<void> {
    const action = this.action();
    if (this.readonly() || action.statusCode === statusCode) return;
    this.displayedStatusCode.set(statusCode);
    try {
      await this.store().transitionStatus(action.id, statusCode);
    } catch (error) {
      this.displayedStatusCode.set(action.statusCode);
      this._messages?.add({
        severity: 'error',
        summary: "Couldn't update action",
        detail: error instanceof Error ? error.message : 'Try again.',
      });
    }
  }

  async openEdit(): Promise<void> {
    if (this.readonly()) return;
    if (!this._actionModal) {
      this._messages?.add({
        severity: 'error',
        summary: "Couldn't open action",
        detail: 'Action editing is unavailable.',
      });
      return;
    }
    await this._actionModal.open(this.action().id, {
      editor: this.store(),
      entryPoint: this.entryPoint(),
    });
  }

  openCase(): void {
    const caseId = this.action().caseId;
    if (caseId) this.goToCase.emit(caseId);
  }
}
